/**
 * Refractor (Prism.js) adapter for `CodeBlock`.
 *
 * **This is the only file in `CodeBlock`'s implementation that knows
 * Refractor / Prism exists.** Everything else operates on the
 * library-agnostic `HighlightedLine` shape returned from this file.
 * Swapping to a different highlighter (e.g. Shiki) should be a
 * single-file change here.
 *
 * Important constraints
 *
 * 1. **No module-scope refractor *value* imports.** All value imports
 *    happen via dynamic `import()` so they end up in separate bundles,
 *    only fetched on demand. (Type-only `import type` declarations are
 *    erased at compile time and have no runtime cost, so they're fine at
 *    module scope.)
 *
 * 2. **Library-agnostic return shape.** Refractor's hast tree is flattened
 *    into `HighlightedLine[]` here, so consumers never depend on hast.
 *
 * 3. **Explicit per-language imports, not template-literal imports.**
 *    Atlaspack does not statically analyze template-literal `import()`
 *    arguments — it leaves them as a runtime `require()` call which
 *    throws `Cannot find module` in the browser. So every grammar must
 *    be referenced by a string-literal `import()` call.
 *
 * 4. **Targets refractor v3** (the version AFM ships, via
 *    `@atlaskit/code`). v3 is CommonJS, exposes its core as
 *    `refractor/core` rather than `refractor/lib/core`, and
 *    `highlight()` returns a flat `RefractorNode[]` rather than a
 *    `root` node.
 */

import type { RefractorNode, RefractorSyntax } from 'refractor/core';

import type { SupportedLanguage } from './supported-languages';
import type { Highlight, HighlightedLine } from './types/highlighted-line';
import { splitLines } from './utils/split-lines';

/**
 * A refractor grammar module — the `RefractorSyntax` function that gets
 * passed to `refractor.register`. refractor v3's grammar modules use
 * `export =`, which dynamic `import()` surfaces as `default`.
 */
type GrammarModule = { default: RefractorSyntax };

/**
 * Map of every `SupportedLanguage` to the dynamic `import()` call that
 * loads its Prism grammar. See the module header for why these MUST
 * be string-literal `import()` calls (not template literals).
 *
 * The Prism grammar name (the import path's last segment) differs
 * from the `SupportedLanguage` key for several entries — e.g.
 * `html → markup`, `cmake → makefile`, `vb → vbnet`, `xml → markup`.
 * When adding a language whose Prism name differs, also add an entry
 * to `PRISM_LANGUAGE_OVERRIDES` below — TypeScript can't enforce the
 * sync between the two maps.
 *
 * When adding a new entry to `SUPPORTED_LANGUAGES`, also add the
 * matching loader here (TypeScript will require it via the
 * `Record<SupportedLanguage, ...>` type).
 */
const GRAMMAR_LOADERS: Record<SupportedLanguage, () => Promise<GrammarModule>> = {
	abap: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-abap" */ 'refractor/lang/abap'
		),
	abnf: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-abnf" */ 'refractor/lang/abnf'
		),
	actionscript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-actionscript" */ 'refractor/lang/actionscript'
		),
	ada: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-ada" */ 'refractor/lang/ada'
		),
	apacheconf: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-apacheconf" */ 'refractor/lang/apacheconf'
		),
	apex: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-apex" */
			// @ts-expect-error -- @types/refractor@3 ships no declaration for this grammar,
			// even though refractor itself provides `lang/apex.js`.
			'refractor/lang/apex'
		),
	applescript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-applescript" */ 'refractor/lang/applescript'
		),
	asciidoc: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-asciidoc" */ 'refractor/lang/asciidoc'
		),
	autoit: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-autoit" */ 'refractor/lang/autoit'
		),
	bash: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-bash" */ 'refractor/lang/bash'
		),
	batch: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-batch" */ 'refractor/lang/batch'
		),
	brainfuck: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-brainfuck" */ 'refractor/lang/brainfuck'
		),
	c: () =>
		import(/* webpackChunkName: "@atlassian/search-common_refractor-lang-c" */ 'refractor/lang/c'),
	clojure: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-clojure" */ 'refractor/lang/clojure'
		),
	cmake: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-makefile" */ 'refractor/lang/makefile'
		),
	coffeescript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-coffeescript" */ 'refractor/lang/coffeescript'
		),
	cpp: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-cpp" */ 'refractor/lang/cpp'
		),
	csharp: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-csharp" */ 'refractor/lang/csharp'
		),
	css: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-css" */ 'refractor/lang/css'
		),
	cypher: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-cypher" */ 'refractor/lang/cypher'
		),
	d: () =>
		import(/* webpackChunkName: "@atlassian/search-common_refractor-lang-d" */ 'refractor/lang/d'),
	dart: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-dart" */ 'refractor/lang/dart'
		),
	diff: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-diff" */ 'refractor/lang/diff'
		),
	dockerfile: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-docker" */ 'refractor/lang/docker'
		),
	elixir: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-elixir" */ 'refractor/lang/elixir'
		),
	erlang: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-erlang" */ 'refractor/lang/erlang'
		),
	excel: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-excel-formula" */ 'refractor/lang/excel-formula'
		),
	fortran: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-fortran" */ 'refractor/lang/fortran'
		),
	fsharp: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-fsharp" */ 'refractor/lang/fsharp'
		),
	gherkin: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-gherkin" */ 'refractor/lang/gherkin'
		),
	glsl: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-glsl" */ 'refractor/lang/glsl'
		),
	go: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-go" */ 'refractor/lang/go'
		),
	graphql: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-graphql" */ 'refractor/lang/graphql'
		),
	groovy: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-groovy" */ 'refractor/lang/groovy'
		),
	haml: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-haml" */ 'refractor/lang/haml'
		),
	handlebars: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-handlebars" */ 'refractor/lang/handlebars'
		),
	haskell: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-haskell" */ 'refractor/lang/haskell'
		),
	haxe: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-haxe" */ 'refractor/lang/haxe'
		),
	hcl: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-hcl" */ 'refractor/lang/hcl'
		),
	html: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-markup" */ 'refractor/lang/markup'
		),
	ini: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-ini" */ 'refractor/lang/ini'
		),
	java: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-java" */ 'refractor/lang/java'
		),
	javascript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-jsx" */ 'refractor/lang/jsx'
		),
	json: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-json" */ 'refractor/lang/json'
		),
	julia: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-julia" */ 'refractor/lang/julia'
		),
	kotlin: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-kotlin" */ 'refractor/lang/kotlin'
		),
	latex: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-latex" */ 'refractor/lang/latex'
		),
	less: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-less" */ 'refractor/lang/less'
		),
	lisp: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-lisp" */ 'refractor/lang/lisp'
		),
	livescript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-livescript" */ 'refractor/lang/livescript'
		),
	lua: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-lua" */ 'refractor/lang/lua'
		),
	makefile: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-makefile" */ 'refractor/lang/makefile'
		),
	markdown: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-markdown" */ 'refractor/lang/markdown'
		),
	objectivec: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-objectivec" */ 'refractor/lang/objectivec'
		),
	ocaml: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-ocaml" */ 'refractor/lang/ocaml'
		),
	pascal: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-pascal" */ 'refractor/lang/pascal'
		),
	perl: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-perl" */ 'refractor/lang/perl'
		),
	php: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-php" */ 'refractor/lang/php'
		),
	powershell: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-powershell" */ 'refractor/lang/powershell'
		),
	prolog: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-prolog" */ 'refractor/lang/prolog'
		),
	protobuf: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-protobuf" */ 'refractor/lang/protobuf'
		),
	pug: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-pug" */ 'refractor/lang/pug'
		),
	python: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-python" */ 'refractor/lang/python'
		),
	qml: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-qml" */ 'refractor/lang/qml'
		),
	r: () =>
		import(/* webpackChunkName: "@atlassian/search-common_refractor-lang-r" */ 'refractor/lang/r'),
	rego: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-rego" */
			// @ts-expect-error -- @types/refractor@3 ships no declaration for this grammar,
			// even though refractor itself provides `lang/rego.js`.
			'refractor/lang/rego'
		),
	restructuredtext: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-rest" */ 'refractor/lang/rest'
		),
	ruby: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-ruby" */ 'refractor/lang/ruby'
		),
	rust: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-rust" */ 'refractor/lang/rust'
		),
	sass: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-sass" */ 'refractor/lang/sass'
		),
	scala: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-scala" */ 'refractor/lang/scala'
		),
	scheme: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-scheme" */ 'refractor/lang/scheme'
		),
	scss: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-scss" */ 'refractor/lang/scss'
		),
	smalltalk: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-smalltalk" */ 'refractor/lang/smalltalk'
		),
	sol: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-solidity" */ 'refractor/lang/solidity'
		),
	sparql: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-sparql" */ 'refractor/lang/sparql'
		),
	sql: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-sql" */ 'refractor/lang/sql'
		),
	stylus: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-stylus" */ 'refractor/lang/stylus'
		),
	swift: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-swift" */ 'refractor/lang/swift'
		),
	tcl: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-tcl" */ 'refractor/lang/tcl'
		),
	tsx: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-tsx" */ 'refractor/lang/tsx'
		),
	twig: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-twig" */ 'refractor/lang/twig'
		),
	typescript: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-typescript" */ 'refractor/lang/typescript'
		),
	vb: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-vbnet" */ 'refractor/lang/vbnet'
		),
	vbnet: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-vbnet" */ 'refractor/lang/vbnet'
		),
	verilog: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-verilog" */ 'refractor/lang/verilog'
		),
	vim: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-vim" */ 'refractor/lang/vim'
		),
	xml: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-markup" */ 'refractor/lang/markup'
		),
	yaml: () =>
		import(
			/* webpackChunkName: "@atlassian/search-common_refractor-lang-yaml" */ 'refractor/lang/yaml'
		),
};

/**
 * Sparse map of `SupportedLanguage` keys whose Prism grammar name
 * differs from the key. Languages not listed here use the key directly
 * as the Prism grammar name. Used for the second argument to
 * `refractor.highlight(code, name)`.
 *
 * **Must stay in sync with the import paths in `GRAMMAR_LOADERS`.**
 * (TypeScript cannot enforce this — both the path string and the
 * grammar name are opaque strings to the compiler.) When adding a new
 * entry whose Prism name differs from its `SupportedLanguage` key, add
 * an entry here AND make sure the matching `GRAMMAR_LOADERS` entry
 * imports `refractor/lang/<prism name>`.
 */
const PRISM_LANGUAGE_OVERRIDES: Partial<Record<SupportedLanguage, string>> = {
	cmake: 'makefile',
	dockerfile: 'docker',
	excel: 'excel-formula',
	html: 'markup',
	javascript: 'jsx',
	restructuredtext: 'rest',
	sol: 'solidity',
	vb: 'vbnet',
	xml: 'markup',
};

const toPrismName = (language: SupportedLanguage): string =>
	PRISM_LANGUAGE_OVERRIDES[language] ?? language;

/**
 * Local hast-node shape used inside the walker. Captures only the
 * properties the walker reads, in a form TypeScript can discriminate
 * on via `node.type`. We can't use refractor's `AST.Text | AST.Element`
 * directly here because both extend `AST.Unist.Node`, whose `type` is a
 * plain `string` — so they don't form a union TypeScript can narrow on
 * via the `type` discriminant.
 *
 * Note: `properties.className` is typed as `string[]` here. That is a
 * refractor-specific invariant — refractor builds its hast tree from
 * grammar tokens and always emits class names as a `string[]`. Hast in
 * general permits a much wider shape for any property
 * (`boolean | number | string | null | undefined | Array<string | number>`),
 * but we only consume refractor's output, so the narrower assumption
 * holds. If we ever switch highlighters to one that produces hast
 * differently, revisit this.
 */
type WalkableNode =
	| { type: 'text'; value: string }
	| {
			type: 'element';
			properties?: { className?: string[] };
			children: WalkableNode[];
	  };

/**
 * Flatten Refractor's hast nodes into a 2D array of tokens, split on
 * newline boundaries.
 *
 * Refractor returns a flat list of hast nodes containing nested
 * `<span>` elements (with class names like `token keyword`) and text
 * nodes. Newlines can appear anywhere — inside text nodes, inside token
 * elements (e.g. multi-line block comments), or between siblings. We
 * walk the nodes depth-first, accumulating tokens, and start a new line
 * whenever we encounter `\n`.
 *
 * @internal Exported only for unit testing. Not part of the package's
 * public API.
 */
export const flattenToLines = (nodes: RefractorNode[]): HighlightedLine[] => {
	const lines: HighlightedLine[] = [[]];

	const walk = (node: WalkableNode, classes: string[]): void => {
		if (node.type === 'text') {
			const segments = splitLines(node.value);

			segments.forEach((segment, i) => {
				const isFirst = i === 0;
				// The 1st segment continues the current line, but every subsequent segment
				// is preceded by a `\n`, so we need to start a new, empty HighlightedLine
				if (!isFirst) {
					lines.push([]);
				}
				if (segment.length > 0) {
					const currentLine = lines.at(-1);
					currentLine?.push(classes.length > 0 ? { text: segment, classes } : { text: segment });
				}
			});
			return;
		}

		const elementClasses = node.properties?.className ?? [];
		/*
		 * Dedupe while preserving order of first occurrence. Prism wraps
		 * nested tokens in `<span class="token X"><span class="token Y">…`,
		 * so without dedup the inner token's class list ends up
		 * `['token', 'X', 'token', 'Y']`. Doing this once here avoids
		 * paying the cost on every render in the consumer.
		 */
		const nextClasses =
			elementClasses.length > 0 ? Array.from(new Set([...classes, ...elementClasses])) : classes;
		node.children.forEach((child) => walk(child, nextClasses));
	};

	/*
	 * see: the code comment with WalkableNode for an explanation of why we have
	 * to cast `as WalkableNode` here
	 */
	nodes.forEach((node) => walk(node as WalkableNode, []));

	return lines;
};

/**
 * Highlight `code` as `language`.
 *
 * Lazily loads Refractor and the requested language's grammar via
 * dynamic `import()` so each ends up in its own bundle, only fetched
 * on demand. Safe to call repeatedly: refractor's internal grammar
 * registry deduplicates registrations and the bundler / browser
 * module cache deduplicate the dynamic imports themselves.
 */
export const highlight: Highlight = async (code, language) => {
	const [refractor, grammarModule] = await Promise.all([
		import(/* webpackChunkName: "@atlassian/search-common_refractor-core" */ 'refractor/core'),
		GRAMMAR_LOADERS[language](),
	]);
	refractor.register(grammarModule.default);
	const nodes = refractor.highlight(code, toPrismName(language));
	return flattenToLines(nodes);
};
